import { useState, useEffect, type ChangeEvent, type ReactNode } from "react";
import Modal from "@modalSrc/Modal";
import ModalFormFields from "@modalSrc/ModalFormFields";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import ConfirmModal from "@elements/modals/ConfirmModal";
import { IoTrashOutline } from "react-icons/io5";
import DateTimePicker from "@elements/DateTimePicker";

type FeeSchedule = {
    id?: number;
    name?: string;
    description?: string;
    effectiveDate?: string;
    expirationDate?: string;
    [key: string]: unknown;
};

type FeeScheduleForm = {
    name: string;
    description: string;
    effectiveDate: string;
    expirationDate: string;
};

type FeeScheduleModalProps = {
    isOpen: boolean;
    schedule: FeeSchedule | null;
    onClose: () => void;
    updateSpinner?: (isLoading: boolean) => void;
};

export default function FeeScheduleModal({ isOpen, schedule, onClose, updateSpinner }: FeeScheduleModalProps) {
    const [form, setForm] = useState<FeeScheduleForm>({
        name: "",
        description: "",
        effectiveDate: "",
        expirationDate: "",
    });
    const [existingSchedule, setExistingSchedule] = useState<FeeSchedule | null>(schedule);
    const [changed, setChanged] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);


    useEffect(() => {
        setExistingSchedule(schedule);
        setForm({
            name: schedule?.name || "",
            description: schedule?.description || "",
            effectiveDate: schedule?.effectiveDate ? schedule.effectiveDate.substring(0, 10) : "",
            expirationDate: schedule?.expirationDate ? schedule.expirationDate.substring(0, 10) : ""
        });
        setChanged(false);
    }, [schedule, isOpen]);

    if (!isOpen) return null;

    async function handleSubmit() {
        // Merge and filter out empty fields
        let newData = { ...(schedule || {}), ...form };
        const filteredData: Record<string, unknown> = {};
        Object.keys(newData).forEach(key => {
            const val = newData[key as keyof typeof newData];
            if (val !== undefined && val !== null && val !== "") {
                filteredData[key] = val;
            }
        });
        updateSpinner && updateSpinner(true);
        try {
            const resp = await RightTimeApi.sendAsync("FeeSchedule", filteredData);
            if (resp && resp.ok) {
                onClose();
            } else {
                toast.error("Failed to save fee schedule");
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : "Unknown error";
            toast.error("Error saving fee schedule: " + message);
        } finally {
            updateSpinner && updateSpinner(false);
        }
    }

    function handleChange(e: ChangeEvent<HTMLInputElement>) {
        const { name, value } = e.target;
        setForm(prevForm => {
            const field = name as keyof FeeScheduleForm;
            const newForm = { ...prevForm, [field]: value };
            // Compare newForm to existingSchedule (handle add case)
            const base = existingSchedule || {};
            // Normalize for add: treat empty object as all blank fields
            const isDifferent = ["name", "description", "effectiveDate", "expirationDate"].some(
                (key) => (newForm[key as keyof FeeScheduleForm] || "") !== ((base[key as keyof FeeSchedule] as string) || "")
            );
            setChanged(isDifferent);
            return newForm;
        });
    }

    function handleDateChange(field: 'effectiveDate' | 'expirationDate', value: string) {
        setForm(prevForm => {
            const newForm = { ...prevForm, [field]: value };
            const base = existingSchedule || {};
            const isDifferent = ["name", "description", "effectiveDate", "expirationDate"].some(
                (key) => (newForm[key as keyof FeeScheduleForm] || "") !== ((base[key as keyof FeeSchedule] as string) || "")
            );
            setChanged(isDifferent);
            return newForm;
        });
    }
    function onDelete() {
        setShowDeleteConfirm(false);
        if (!schedule || !schedule.id) return;
        updateSpinner(true);
        RightTimeApi.deleteAsync(`/FeeSchedule/${schedule.id}`)
            .then(resp => {
                if (resp && resp.ok) {
                    onClose();
                } else {
                    toast.error("Failed to delete fee schedule");
                }
            })
            .catch(err => {
                const message = err instanceof Error ? err.message : "Unknown error";
                toast.error("Error deleting fee schedule: " + message);
            })
            .finally(() => {
                updateSpinner(false);
            });
    }


    const fields: Record<string, ReactNode> = {
        "Name": <input name="name" value={form.name} onChange={handleChange} required />,
        "Description": <input name="description" value={form.description} onChange={handleChange} />,
        "Effective Date": (
            <DateTimePicker
                field="effectiveDate"
                pickerTitle="Effective Date"
                value={form.effectiveDate}
                onChange={(e) => handleDateChange('effectiveDate', e?.target?.value || '')}
                dateOnly
                name="effectiveDate"
            />
        ),
        "Expiration Date": (
            <DateTimePicker
                field="expirationDate"
                pickerTitle="Expiration Date"
                value={form.expirationDate}
                onChange={(e) => handleDateChange('expirationDate', e?.target?.value || '')}
                dateOnly
                name="expirationDate"
            />
        )
    };

    const requiredFilled = form.name && form.name.trim().length > 0;
    const footer = (
        <>
            <div className="footerLeft">
                <button
                    type="button"
                    className={`btn btn-danger iconButton ${!schedule || !schedule.id ? ' hidden' : ''}`}
                    onClick={() => setShowDeleteConfirm(true)}
                    title="Delete Fee Schedule"
                    aria-label="Delete Fee Schedule"
                    disabled={!schedule || !schedule.id}
                ><IoTrashOutline /></button>
            </div>
            <div className="footerRight">
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={!changed || !requiredFilled}
                >Save</button>
            </div>
        </>
    );

    return (
        <>
            <Modal
                isOpen={isOpen}
                title={schedule && schedule.id ? "Edit Fee Schedule" : "Add Fee Schedule"}
                onClose={onClose}
                footer={footer}
                className="feeScheduleModal"
            >
                <ModalFormFields fields={fields} />
            </Modal>
            <ConfirmModal
                isOpen={showDeleteConfirm}
                onConfirm={onDelete}
                onCancel={() => setShowDeleteConfirm(false)}
                title="Delete Fee Schedule"
                message={`Are you sure you want to delete the ${schedule?.name} fee schedule?`}
                confirmText="Yes, Delete Fee Schedule"
                dangerText="This will permanently remove the fee schedule and all associated data."
            />
        </>
    );
}


